import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Alert,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";
// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import {
  SequenceNumberInput,
  ActivityButton,
} from "../../../components/Common";
import {
  CurrencyEnum,
  ErrorHandlingTypeEnum,
  SequenceEnum,
} from "../../../enums";
import { ParsedError } from "../../../interfaces";
import {
  CreatePlanRequest,
  PlanDescriptionFeature,
} from "../../../interfaces/plan";

import {
  useGetPlanQuery,
  useAddPlanMutation,
  useUpdatePlanMutation,
} from "../../../store/api/planApi";
import { FeaturesSelect } from "../../../components/Selects/FeaturesSelect";
import { SelectListDto } from "../../../models";

const createEmptyDescriptionFeature = (
  sequence = 1,
): PlanDescriptionFeature => ({
  sequence,
  available: true,
  text: { en: "", ar: "" },
});

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  courseId: string;
  gradeId: string;
};
export const AddEditPlanModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  courseId,
  gradeId,
}) => {
  const { t, i18n } = useTranslation();
  const [request] = useState<CreatePlanRequest>();
  const [selectedFeatures, setSelectedFeatures] = useState<any[]>();

  const {
    data: plan,
    error: errorPlan,
    isLoading: isLoadingPlan,
  } = useGetPlanQuery(id as string, { skip: !id });

  const [
    addPlan,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddPlanMutation();

  const [
    updatePlan,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdatePlanMutation();

  //#region form
  const form = useFormik<CreatePlanRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      description: {
        en: request?.description?.en || "",
        ar: request?.description?.ar || "",
      },
      courseId: request?.courseId || courseId,
      gradeId: request?.gradeId || gradeId,
      sequenceNumber: request?.sequenceNumber || 0,
      price: request?.price || 0,
      currency: request?.currency || CurrencyEnum.KWD,
      media: request?.media || [],
      isWebPaymentProcess: request?.isWebPaymentProcess || false,
      isWebListing: request?.isWebListing || false,
      isTopSelling: request?.isTopSelling || false,
      features: request?.features || [],
      descriptionFeatures: request?.descriptionFeatures || [],
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      description: Yup.object({
        en: Yup.string()
          .max(
            500,
            t("maxLength", { maxLength: 500, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            500,
            t("maxLength", { maxLength: 500, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      courseId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "grade",
        }),
      ),
      gradeId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "grade",
        }),
      ),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        }),
      ),
      price: Yup.number()
        .typeError(t("priceMustBeGreaterThanZero"))
        .moreThan(0, t("priceMustBeGreaterThanZero"))
        .required(t("isNotEmpty", { displayName: "price" })),
      currency: Yup.string()
        .oneOf(Object.values(CurrencyEnum))
        .required(t("isNotEmpty", { displayName: "currency" })),
      media: Yup.array().of(Yup.string()),
      isWebPaymentProcess: Yup.boolean(),
      isWebListing: Yup.boolean(),
      isTopSelling: Yup.boolean(),
      features: Yup.array().of(Yup.string()),
      descriptionFeatures: Yup.array().of(
        Yup.object({
          sequence: Yup.number().required(
            t("isNotEmpty", { displayName: "sequenceNumber" }),
          ),
          available: Yup.boolean(),
          text: Yup.object({
            en: Yup.string()
              .max(
                500,
                t("maxLength", { maxLength: 500, displayName: "englishValue" }),
              )
              .required(
                t("isNotEmpty", {
                  displayName: "englishValue",
                }),
              ),
            ar: Yup.string()
              .max(
                500,
                t("maxLength", { maxLength: 500, displayName: "arabicValue" }),
              )
              .required(
                t("isNotEmpty", {
                  displayName: "arabicValue",
                }),
              ),
          }),
        }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        const descriptionFeatures = (values.descriptionFeatures || []).map(
          (feature, index) => ({
            ...feature,
            sequence: Number(feature.sequence) || index + 1,
            available: !!feature.available,
          }),
        );

        const payload = {
          ...values,
          sequenceNumber: Number(values.sequenceNumber),
          price: Number(values.price),
          currency: values.currency,
          features: selectedFeatures?.map((sf) => sf.value) || [],
          descriptionFeatures,
        };

        if (id) {
          await updatePlan({
            id,
            data: payload,
          }).unwrap();
        } else {
          await addPlan(payload).unwrap();
        }
        onClose();
      } catch (err) {
        // error handled by RTK Query
      }
    },
  });
  //#endregion

  //#region effects

  useEffect(() => {
    if (id && plan) {
      form.setValues({
        name: plan.name,
        description: plan.description,
        courseId: courseId,
        gradeId: plan?.gradeId,
        sequenceNumber: plan?.sequenceNumber || 0,
        price: plan.price,
        currency: plan.currency || CurrencyEnum.KWD,
        media: [],
        isWebPaymentProcess: plan.isWebPaymentProcess,
        isWebListing: plan.isWebListing,
        isTopSelling: plan.isTopSelling,
        features: plan.features.map((f) => f.id),
        descriptionFeatures: (plan.descriptionFeatures || []).map(
          (feature, index) => ({
            sequence: feature.sequence ?? index + 1,
            available: feature.available ?? true,
            text: {
              en: feature.text?.en || "",
              ar: feature.text?.ar || "",
            },
          }),
        ),
      });
      setSelectedFeatures(
        plan.features.map((item: SelectListDto) => {
          return {
            value: item.id,
            label: i18n.language === "ar" ? item.value.ar : item.value.en,
          };
        }),
      );
    }
  }, [plan, isLoadingPlan, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
    setSelectedFeatures([]);
  };

  const onChangeFeatures = (changedValue: any) => {
    setSelectedFeatures(changedValue);
  };

  const addDescriptionFeature = () => {
    const current = form.values.descriptionFeatures || [];
    const nextSequence =
      current.length > 0
        ? Math.max(...current.map((f) => Number(f.sequence) || 0)) + 1
        : 1;
    form.setFieldValue("descriptionFeatures", [
      ...current,
      createEmptyDescriptionFeature(nextSequence),
    ]);
  };

  const removeDescriptionFeature = (index: number) => {
    const current = [...(form.values.descriptionFeatures || [])];
    current.splice(index, 1);
    form.setFieldValue("descriptionFeatures", current);
  };

  const getDescriptionFeatureError = (
    index: number,
    path: "sequence" | "text.en" | "text.ar",
  ): string | undefined => {
    const errors = form.errors.descriptionFeatures?.[index];
    if (!errors || typeof errors === "string") return undefined;
    if (path === "sequence") return errors.sequence as string | undefined;
    if (path === "text.en")
      return (errors.text as { en?: string } | undefined)?.en;
    return (errors.text as { ar?: string } | undefined)?.ar;
  };

  const isDescriptionFeatureTouched = (
    index: number,
    path: "sequence" | "text.en" | "text.ar",
  ): boolean => {
    const touched = form.touched.descriptionFeatures?.[index];
    if (!touched || typeof touched === "boolean") return !!touched;
    if (path === "sequence") return !!touched.sequence;
    if (path === "text.en")
      return !!(touched.text as { en?: boolean } | undefined)?.en;
    return !!(touched.text as { ar?: boolean } | undefined)?.ar;
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        size="xl"
        scrollable
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="py-2 px-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "plan" })
              : t("addItem", { item: "plan" })}
          </ModalHeader>

          <ModalBody className="py-3 px-3" style={{ maxHeight: "75vh" }}>
            <Alert color="info" className="mb-3">
              <div className="fw-semibold mb-1">{t("planGuideTitle")}</div>
              <ul className="mb-0 ps-3">
                <li>{t("planGuideRank")}</li>
                <li>{t("planGuideUnique")}</li>
                <li>{t("planGuideInheritance")}</li>
                <li>{t("planGuidePrice")}</li>
                <li>{t("planGuideHigherPrice")}</li>
                <li>{t("planGuideCurrency")}</li>
              </ul>
            </Alert>
            <Row className="g-2 mb-2">
              <Col md={4}>
                <SequenceNumberInput
                  tabIndex={1}
                  value={form.values.sequenceNumber}
                  onChange={(e) => {
                    e.preventDefault();
                    const { value } = e.target;
                    const regex =
                      /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                    if (regex.test(value.toString())) {
                      form.setFieldValue("sequenceNumber", value);
                    } else {
                      form.setFieldValue("sequenceNumber", "");
                    }
                  }}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.sequenceNumber && form.errors.sequenceNumber
                      ? true
                      : false
                  }
                  sequenceType={SequenceEnum.PLAN}
                  onGenerateSequenceNumber={(v: number) => {
                    form.setFieldValue("sequenceNumber", v);
                  }}
                  mode={id ? "edit" : "add"}
                  request={{
                    type: SequenceEnum.PLAN,
                    courseId: courseId,
                    gradeId: gradeId,
                  }}
                />
                {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
                  <FormFeedback type="invalid" className="d-block">
                    {form.errors.sequenceNumber as string}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md={4}>
                <label htmlFor="price" className="form-label mb-1">
                  {t("price")}
                  <span className="text-danger">*</span>
                </label>
                <Input
                  onChange={(e) => {
                    e.preventDefault();
                    const { value } = e.target;
                    const regex =
                      /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                    if (regex.test(value.toString())) {
                      form.setFieldValue("price", value);
                    } else {
                      form.setFieldValue("price", "");
                    }
                  }}
                  autoComplete="off"
                  id="price"
                  name="price"
                  value={form.values?.price}
                  type="text"
                  className="form-control"
                  placeholder={t("price")}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.price && form.errors.price ? true : false
                  }
                />
                {form.touched.price && form.errors.price ? (
                  <FormFeedback type="invalid">
                    {form.errors.price as string}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md={4}>
                <label htmlFor="currency" className="form-label mb-1">
                  {t("currency")}
                  <span className="text-danger">*</span>
                </label>
                <Input
                  type="select"
                  id="currency"
                  name="currency"
                  value={form.values.currency}
                  className="form-select"
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.currency && form.errors.currency ? true : false
                  }
                >
                  <option value={CurrencyEnum.KWD}>Kuwait (KWD)</option>
                  <option value={CurrencyEnum.SAR}>KSA (SAR)</option>
                  <option value={CurrencyEnum.AED}>UAE (AED)</option>
                  <option value={CurrencyEnum.BHD}>Bahrain (BHD)</option>
                  <option value={CurrencyEnum.QAR}>Qatar (QAR)</option>
                  <option value={CurrencyEnum.OMR}>Oman (OMR)</option>
                  <option value={CurrencyEnum.EGP}>Egypt (EGP)</option>
                  <option value={CurrencyEnum.USD}>USD</option>
                </Input>
                {form.touched.currency && form.errors.currency ? (
                  <FormFeedback type="invalid">
                    {form.errors.currency as string}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <Row className="g-2 mb-2">
              <Col md={6}>
                <label htmlFor="name.en" className="form-label mb-1">
                  {t("nameEn")}
                  <span className="text-danger">*</span>
                </label>
                <Input
                  disabled={!!errorAdd}
                  autoComplete="off"
                  id="name.en"
                  name="name.en"
                  value={form.values?.name.en}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInEnglish")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.name?.en && form.errors.name?.en ? true : false
                  }
                />
                {form.touched.name?.en && form.errors.name?.en ? (
                  <FormFeedback type="invalid">
                    {form.errors.name?.en as string}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <label htmlFor="name.ar" className="form-label mb-1">
                  {t("nameAr")}
                  <span className="text-danger">*</span>
                </label>
                <Input
                  style={{ fontFamily: "aljannat", direction: "rtl" }}
                  disabled={!!errorAdd}
                  autoComplete="off"
                  id="name.ar"
                  name="name.ar"
                  value={form.values.name?.ar}
                  type="text"
                  className="form-control"
                  placeholder={t("enterInArabic")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.name?.ar && form.errors.name?.ar ? true : false
                  }
                />
                {form.touched.name?.ar && form.errors.name?.ar ? (
                  <FormFeedback type="invalid">
                    {form.errors.name?.ar as string}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <Row className="g-2 mb-2">
              <Col md={6}>
                <label htmlFor="description.en" className="form-label mb-1">
                  {t("descriptionEn")}
                  <span className="text-danger">*</span>
                </label>
                <Input
                  disabled={!!errorAdd}
                  autoComplete="off"
                  id="description.en"
                  name="description.en"
                  value={form.values?.description.en}
                  type="textarea"
                  rows={5}
                  className="form-control"
                  placeholder={t("enterInEnglish")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.description?.en && form.errors.description?.en
                      ? true
                      : false
                  }
                />
                {form.touched.description?.en && form.errors.description?.en ? (
                  <FormFeedback type="invalid">
                    {form.errors.description?.en as string}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <label htmlFor="description.ar" className="form-label mb-1">
                  {t("descriptionAr")}
                  <span className="text-danger">*</span>
                </label>
                <Input
                  style={{ fontFamily: "aljannat", direction: "rtl" }}
                  disabled={!!errorAdd}
                  autoComplete="off"
                  id="description.ar"
                  name="description.ar"
                  value={form.values?.description.ar}
                  type="textarea"
                  rows={5}
                  className="form-control"
                  placeholder={t("enterInArabic")}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.description?.ar && form.errors.description?.ar
                      ? true
                      : false
                  }
                />
                {form.touched.description?.ar && form.errors.description?.ar ? (
                  <FormFeedback type="invalid">
                    {form.errors.description?.ar as string}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <div className="d-flex flex-wrap gap-3 align-items-center mb-2 py-1">
              <div className="form-check mb-0">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="isWebPaymentProcess"
                  checked={form.values.isWebPaymentProcess}
                  value={(form.values.isWebPaymentProcess || false) as any}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                />
                <Label
                  className="form-check-label"
                  htmlFor="isWebPaymentProcess"
                >
                  {t("webPaymentProcess")}
                </Label>
              </div>
              <div className="form-check mb-0">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="isWebListing"
                  checked={form.values.isWebListing}
                  value={(form.values.isWebListing || false) as any}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                />
                <Label className="form-check-label" htmlFor="isWebListing">
                  {t("listOnWebHomePage")}
                </Label>
              </div>
              <div className="form-check mb-0">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="isTopSelling"
                  checked={form.values.isTopSelling}
                  value={(form.values.isTopSelling || false) as any}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                />
                <Label className="form-check-label" htmlFor="isTopSelling">
                  {t("topSelling")}
                </Label>
              </div>
            </div>

            <div className="mb-2">
              <label className="form-label mb-1">{t("features")}</label>
              <FeaturesSelect
                isMulti={true}
                closeMenuOnSelect={false}
                value={selectedFeatures}
                onChange={onChangeFeatures}
                placeholder="selectFeatures"
              />
            </div>

            <div className="mb-0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">
                  {t("featuresDescription")}
                </label>
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  onClick={addDescriptionFeature}
                >
                  <i className="ri-add-line me-1 align-middle" />
                  {t("add")}
                </Button>
              </div>

              {(form.values.descriptionFeatures || []).length === 0 ? (
                <div className="text-muted fs-13 border rounded px-3 py-2">
                  {t("featuresDescriptionEmpty")}
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {(form.values.descriptionFeatures || []).map(
                    (feature, index) => (
                      <div
                        key={`description-feature-${index}`}
                        className="border rounded px-2 py-2"
                      >
                        <Row className="g-2 align-items-end">
                          <Col xs={4} md={1}>
                            <label
                              htmlFor={`descriptionFeatures.${index}.sequence`}
                              className="form-label mb-1 fs-12"
                            >
                              {t("sequenceNumber")}
                            </label>
                            <Input
                              id={`descriptionFeatures.${index}.sequence`}
                              name={`descriptionFeatures.${index}.sequence`}
                              type="text"
                              bsSize="sm"
                              value={feature.sequence}
                              onChange={(e) => {
                                const { value } = e.target;
                                const regex =
                                  /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                                if (
                                  regex.test(value.toString()) ||
                                  value === ""
                                ) {
                                  form.setFieldValue(
                                    `descriptionFeatures.${index}.sequence`,
                                    value === "" ? "" : Number(value),
                                  );
                                }
                              }}
                              onBlur={form.handleBlur}
                              invalid={
                                isDescriptionFeatureTouched(index, "sequence") &&
                                !!getDescriptionFeatureError(index, "sequence")
                              }
                            />
                          </Col>
                          <Col xs={8} md={2} className="d-flex align-items-center pb-1">
                            <div className="form-check mb-0">
                              <Input
                                className="form-check-input"
                                type="checkbox"
                                id={`descriptionFeatures.${index}.available`}
                                name={`descriptionFeatures.${index}.available`}
                                checked={!!feature.available}
                                onChange={(e) => {
                                  form.setFieldValue(
                                    `descriptionFeatures.${index}.available`,
                                    e.target.checked,
                                  );
                                }}
                                onBlur={form.handleBlur}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`descriptionFeatures.${index}.available`}
                              >
                                {t("available")}
                              </Label>
                            </div>
                          </Col>
                          <Col xs={12} md={4}>
                            <label
                              htmlFor={`descriptionFeatures.${index}.text.en`}
                              className="form-label mb-1 fs-12"
                            >
                              {t("descriptionEn")}
                            </label>
                            <Input
                              id={`descriptionFeatures.${index}.text.en`}
                              name={`descriptionFeatures.${index}.text.en`}
                              type="text"
                              bsSize="sm"
                              value={feature.text?.en || ""}
                              placeholder={t("enterInEnglish")}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              invalid={
                                isDescriptionFeatureTouched(index, "text.en") &&
                                !!getDescriptionFeatureError(index, "text.en")
                              }
                            />
                          </Col>
                          <Col xs={10} md={4}>
                            <label
                              htmlFor={`descriptionFeatures.${index}.text.ar`}
                              className="form-label mb-1 fs-12"
                            >
                              {t("descriptionAr")}
                            </label>
                            <Input
                              style={{
                                fontFamily: "aljannat",
                                direction: "rtl",
                              }}
                              id={`descriptionFeatures.${index}.text.ar`}
                              name={`descriptionFeatures.${index}.text.ar`}
                              type="text"
                              bsSize="sm"
                              value={feature.text?.ar || ""}
                              placeholder={t("enterInArabic")}
                              onChange={form.handleChange}
                              onBlur={form.handleBlur}
                              invalid={
                                isDescriptionFeatureTouched(index, "text.ar") &&
                                !!getDescriptionFeatureError(index, "text.ar")
                              }
                            />
                          </Col>
                          <Col
                            xs={2}
                            md={1}
                            className="d-flex justify-content-end pb-1"
                          >
                            <Button
                              type="button"
                              color="danger"
                              outline
                              size="sm"
                              className="btn-icon"
                              title={t("remove")}
                              onClick={() => removeDescriptionFeature(index)}
                            >
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </Col>
                        </Row>
                        {(isDescriptionFeatureTouched(index, "sequence") &&
                          getDescriptionFeatureError(index, "sequence")) ||
                        (isDescriptionFeatureTouched(index, "text.en") &&
                          getDescriptionFeatureError(index, "text.en")) ||
                        (isDescriptionFeatureTouched(index, "text.ar") &&
                          getDescriptionFeatureError(index, "text.ar")) ? (
                          <div className="text-danger fs-12 mt-1">
                            {getDescriptionFeatureError(index, "sequence") ||
                              getDescriptionFeatureError(index, "text.en") ||
                              getDescriptionFeatureError(index, "text.ar")}
                          </div>
                        ) : null}
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter className="py-2 px-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </button>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoadingAdd || isLoadingUpdate}
              disabled={isLoadingAdd || isLoadingUpdate || !!errorPlan}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
